// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "How you use it" in Settings → Assistant. The last 14 days from the
// server's usage log: conversations, what happened to review cards (and which
// fields people change before confirming, the clearest sign of a wrong guess),
// and the chat suggestion buttons. Staff also get the team's numbers.

import KonvoConnections from '@app/features/konvo_connections/state/KonvoConnections';
import {
	cardUsageLines,
	fetchTeamUsage,
	fetchUsageExport,
	formatReplyTime,
	type KonvoAssistantUsage,
	type KonvoTeamUsage,
	usageExportFileName,
} from '@app/features/konvo_connections/state/KonvoUsage';
import {downloadTextFile} from '@app/features/platform/utils/DownloadFile';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {DownloadSimpleIcon} from '@app/features/ui/icons/Icons';
import styles from '@app/features/user/components/modals/tabs/KonvoAssistantProactiveSection.module.css';
import Users from '@app/features/user/state/Users';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

const CARD_KINDS: Record<string, MessageDescriptor> = {
	task: msg({message: 'Tasks', comment: 'Usage stats row label: review cards that create a ClickUp task.'}),
	task_update: msg({message: 'Task updates', comment: 'Usage stats row label: review cards that change a task.'}),
	mail: msg({message: 'Emails', comment: 'Usage stats row label: review cards that send an email.'}),
	mail_manage: msg({message: 'Email actions', comment: 'Usage stats row label: archive or mark-read cards.'}),
	message: msg({message: 'Messages', comment: 'Usage stats row label: review cards that send a chat message.'}),
	whatsapp: msg({message: 'WhatsApp', comment: 'Usage stats row label: review cards that send a WhatsApp message.'}),
	calendar: msg({message: 'Calendar changes', comment: 'Usage stats row label: review cards that change an event.'}),
	time: msg({message: 'Time entries', comment: 'Usage stats row label: review cards that log ClickUp time.'}),
	op: msg({
		message: 'Other actions',
		comment: 'Usage stats row label: review cards for smaller app actions (move mail, mute a chat, change a setting).',
	}),
	web_form: msg({message: 'Web forms', comment: 'Usage stats row label: review cards that send a form on a website.'}),
};
const CARD_LINE_DESCRIPTOR = msg({
	message: '{confirmed} confirmed ({edited} changed first) · {cancelled} cancelled · {ignored} left alone',
	comment: 'Usage stats for one kind of review card over 14 days.',
});
const MOST_CHANGED_DESCRIPTOR = msg({
	message: 'Most changed: {fields}',
	comment: 'Which fields people edit on this kind of card before confirming; {fields} is a comma list.',
});
const TURNS_DESCRIPTOR = msg({
	message: '{count} conversations · typical reply {time} · stopped {stopped}',
	comment: 'Usage stats line for Assistant conversation turns over 14 days.',
});
const STRIP_DESCRIPTOR = msg({
	message: 'Chat suggestions: {created} used · {asked} handed to the Assistant · {dismissed} dismissed',
	comment: 'Usage stats line for the suggestion strip above the composer over 14 days.',
});
const TASK_FORM_DESCRIPTOR = msg({
	message: 'Drafted tasks: {created} created, {untouched} without changes',
	comment: 'Usage stats line: create-task forms that opened with an assistant draft.',
});
const TEAM_ROW_DESCRIPTOR = msg({
	message:
		'{name}: {turns} conversations · {confirmed} cards confirmed, {edited} changed first · {used} suggestions used',
	comment: 'One person in the staff-only team usage list over the chosen days.',
});
const EXPORT_FAILED_DESCRIPTOR = msg({
	message: 'Could not download your usage. Try again.',
	comment: 'Toast when the usage export fails.',
});
const TEAM_FAILED_DESCRIPTOR = msg({
	message: 'Team usage is only available to staff.',
	comment: 'Toast when the team usage request is refused or fails.',
});

function totals(usage: KonvoAssistantUsage) {
	const cards = Object.values(usage.cards);
	const strip = Object.values(usage.strip);
	return {
		confirmed: cards.reduce((sum, card) => sum + card.confirmed, 0),
		edited: cards.reduce((sum, card) => sum + card.edited, 0),
		used: strip.reduce((sum, entry) => sum + entry.create + entry.ask_assistant, 0),
	};
}

export const KonvoAssistantUsageSection = observer(() => {
	const {i18n} = useLingui();
	const usage = KonvoConnections.engine?.usage;
	const [busy, setBusy] = useState<'export' | 'team' | null>(null);
	const [team, setTeam] = useState<KonvoTeamUsage | null>(null);
	const staff = Users.currentUser?.isStaff() === true;
	const lines = cardUsageLines(usage);
	const strip = Object.values(usage?.strip ?? {}).reduce(
		(sum, entry) => ({
			created: sum.created + entry.create,
			asked: sum.asked + entry.ask_assistant,
			dismissed: sum.dismissed + entry.dismiss,
		}),
		{created: 0, asked: 0, dismissed: 0},
	);
	const empty =
		!usage || (usage.turns.count === 0 && lines.length === 0 && strip.created + strip.asked + strip.dismissed === 0);
	const download = () => {
		setBusy('export');
		void fetchUsageExport()
			.then((json) => downloadTextFile(json, usageExportFileName(new Date()), 'application/json'))
			.catch(() => ToastCommands.error(i18n._(EXPORT_FAILED_DESCRIPTOR)))
			.finally(() => setBusy(null));
	};
	const loadTeam = () => {
		setBusy('team');
		void fetchTeamUsage()
			.then((result) => (result ? setTeam(result) : ToastCommands.error(i18n._(TEAM_FAILED_DESCRIPTOR))))
			.finally(() => setBusy(null));
	};
	return (
		<div className={styles.group} data-flx="user.konvo-assistant-tab.usage">
			<h3 className={styles.groupTitle}>
				<Trans>How you use it (last 14 days)</Trans>
			</h3>
			{empty ? (
				<p className={styles.note}>
					<Trans>Nothing yet. The numbers appear as you use the Assistant.</Trans>
				</p>
			) : (
				<>
					{usage && usage.turns.count > 0 && (
						<p className={styles.note} data-flx="user.konvo-assistant-tab.usage.turns">
							{i18n._(TURNS_DESCRIPTOR, {
								count: usage.turns.count,
								time: formatReplyTime(usage.turns.p50_ms) ?? '–',
								stopped: usage.turns.stopped,
							})}
						</p>
					)}
					{lines.map((line) => (
						<p key={line.kind} className={styles.stats} data-flx={`user.konvo-assistant-tab.usage.card.${line.kind}`}>
							<span className={styles.statsText}>
								<strong>{CARD_KINDS[line.kind] ? i18n._(CARD_KINDS[line.kind]!) : line.kind}</strong>{' '}
								{i18n._(CARD_LINE_DESCRIPTOR, {
									confirmed: line.confirmed,
									edited: line.edited,
									cancelled: line.cancelled,
									ignored: line.ignored,
								})}
								{line.fields.length > 0 && ` · ${i18n._(MOST_CHANGED_DESCRIPTOR, {fields: line.fields.join(', ')})}`}
							</span>
						</p>
					))}
					{strip.created + strip.asked + strip.dismissed > 0 && (
						<p className={styles.note} data-flx="user.konvo-assistant-tab.usage.strip">
							{i18n._(STRIP_DESCRIPTOR, strip)}
						</p>
					)}
					{usage && usage.task_form.from_draft > 0 && (
						<p className={styles.note} data-flx="user.konvo-assistant-tab.usage.task-form">
							{i18n._(TASK_FORM_DESCRIPTOR, usage.task_form)}
						</p>
					)}
				</>
			)}
			<div className={styles.export}>
				<Button
					variant="secondary"
					small
					leftIcon={<DownloadSimpleIcon size={16} />}
					submitting={busy === 'export'}
					onClick={download}
					data-flx="user.konvo-assistant-tab.usage.download"
				>
					<Trans>Download my usage</Trans>
				</Button>
				{staff && (
					<Button
						variant="secondary"
						small
						submitting={busy === 'team'}
						onClick={loadTeam}
						data-flx="user.konvo-assistant-tab.usage.team"
					>
						<Trans>Team usage</Trans>
					</Button>
				)}
			</div>
			{team && (
				<div data-flx="user.konvo-assistant-tab.usage.team-list">
					{team.people.map((person) => {
						const sum = totals(person.stats);
						return (
							<p key={person.user_id} className={styles.note}>
								{i18n._(TEAM_ROW_DESCRIPTOR, {
									name: person.name,
									turns: person.stats.turns.count,
									confirmed: sum.confirmed,
									edited: sum.edited,
									used: sum.used,
								})}
							</p>
						);
					})}
				</div>
			)}
			<p className={styles.note}>
				<Trans>
					Konvo keeps these numbers for 90 days and the drafts behind them for 30, to learn where it guesses wrong. The
					download has your own events; team usage shows numbers only, never text.
				</Trans>
			</p>
		</div>
	);
});
